import { useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import { CURRENT_USER_FEATUREBASE_WIDGET_TOKEN } from "api";
import config from "config";
import { useStores } from "contexts";

type FeaturebaseBootOptions = {
  appId: string;
  theme?: string;
  language?: string;
  email?: string;
  userId?: string;
  featurebaseJwt?: string;
};

type FeaturebaseFn = {
  (...args: any[]): void;
  q?: IArguments[];
};

declare global {
  interface Window {
    Featurebase?: FeaturebaseFn;
  }
}

const FEATUREBASE_SCRIPT_ID = "featurebase-sdk";
const FEATUREBASE_SCRIPT_SRC = "https://do.featurebase.app/js/sdk.js";

const ensureFeaturebaseStub = () => {
  if (typeof window.Featurebase === "function") return;

  window.Featurebase = function () {
    (window.Featurebase!.q = window.Featurebase!.q || []).push(arguments);
  };
};

const ensureFeaturebaseScript = () => {
  if (document.getElementById(FEATUREBASE_SCRIPT_ID)) return;

  const script = document.createElement("script");
  script.id = FEATUREBASE_SCRIPT_ID;
  script.src = FEATUREBASE_SCRIPT_SRC;
  script.async = true;
  document.body.appendChild(script);
};

export const FeaturebaseScript = () => {
  const { userStore } = useStores();
  const [fetchAuthToken] = useLazyQuery(CURRENT_USER_FEATUREBASE_WIDGET_TOKEN);

  useEffect(() => {
    const bootFeaturebase = async () => {
      if (!config.featurebaseAppId) return;

      ensureFeaturebaseStub();
      ensureFeaturebaseScript();

      const bootOptions: FeaturebaseBootOptions = {
        appId: config.featurebaseAppId,
        theme: config.featurebaseTheme,
        language: config.featurebaseLanguage,
      };

      const currentUser = userStore.platformStore?.currentUser;
      if (currentUser?.emailAddress) {
        bootOptions.email = currentUser.emailAddress;
        bootOptions.userId = String(currentUser.id || userStore.userId || "");
        const { data } = await fetchAuthToken();
        if (data?.currentUserFeaturebaseWidgetToken) {
          bootOptions.featurebaseJwt = data.currentUserFeaturebaseWidgetToken;
        }
      }

      window.Featurebase?.("boot", bootOptions);
    };

    bootFeaturebase();

    return () => {
      window.Featurebase?.("shutdown");
      window.Featurebase?.("destroy");
    };
  }, [fetchAuthToken, userStore.platformStore?.currentUser, userStore.userId]);

  return null;
};
